import { useEffect, useState } from "react";
import BeaumontHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Beaumont?", a: "We offer Beaumont-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type and document completeness." },
  { q: "Do I need to visit an office in Beaumont?", a: "No. Our service is remote: you complete forms online, visit a local acceptance facility (like a Beaumont post office or Jefferson County clerk) for in-person verification if needed, then ship documents to us via overnight courier." },
  { q: "Where is the nearest passport acceptance facility in Beaumont?", a: "Beaumont has acceptance facilities at the Beaumont Main Post Office, the North Park Post Office, and the Jefferson County Clerk's office. Port Arthur and Nederland also have post office acceptance facilities. We provide a curated list with hours and contact information." },
  { q: "Is there a passport agency near Beaumont?", a: "The nearest passport agency is in Houston, approximately 85 miles west of Beaumont. There is no passport agency in the Golden Triangle area. For urgent needs, our registered courier service is the fastest legally available channel — as quick as 24 hours, without the drive to Houston." },
  { q: "How much does expedited passport service cost in Beaumont?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee covering document review, overnight shipping, and expedited submission. We offer a best price guarantee." },
  { q: "What documents do Beaumont applicants need?", a: "For a new adult passport: proof of citizenship, government-issued photo ID, DS-11 form, and passport photo. For renewals: your most recent passport, one photo, and DS-82 form. For minors: both parents or notarized consent, birth certificate, and photos." },
  { q: "Can I get a passport photo in Beaumont?", a: "Yes. Several Beaumont CVS, Walgreens, and Walmart locations offer passport photos. We recommend confirming State Department photo standard compliance before visiting." },
  { q: "How do I track my Beaumont passport application?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov." },
  { q: "What are the main reasons Beaumont residents need expedited passports?", a: "Southeast Texas petrochemical industry deployments, Galveston cruise departures, Lamar University study abroad programs, and medical tourism to Mexico are the most common reasons. Our courier service eliminates the need for the 85-mile drive to Houston." },
  { q: "Can you help with Louisiana border travel questions?", a: "Yes. Southeast Texas residents frequently travel through Baton Rouge and New Orleans for international connections. We can help you understand passport requirements for all Gulf Coast international travel patterns." },
];

function BeaumontStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (<a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>))}
      </div>
    </div>
  );
}

function BeaumontProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Beaumont Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Beaumont and Southeast Texas travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Beaumont travelers departing within 3 days. Full document review and overnight courier submission — no drive to Houston required." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For petrochemical industry travelers and families with travel in 1–2 weeks. Balanced speed and value." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Beaumont residents planning ahead. Significantly faster than standard post office processing (10–13 weeks)." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeaumontServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Beaumont travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Beaumont residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Beaumont adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Golden Triangle residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Beaumont families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport for frequent Beaumont petrochemical and business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors before State Department submission." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Beaumont Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Complete passport solutions for the Golden Triangle — petrochemical engineers, Lamar University students, Gulf Coast families, and Southeast Texas travelers.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeaumontLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Beaumont</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Beaumont is the largest city in Southeast Texas — the commercial and industrial hub of the "Golden Triangle" formed by Beaumont, Port Arthur, and Orange. With a population approaching 120,000, Beaumont anchors a metro area of over 400,000 people whose economy is dominated by the petrochemical refining industry, healthcare, and Lamar University.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Golden Triangle is home to one of the highest concentrations of petrochemical refineries in the world. ExxonMobil, Chevron Phillips, BASF, and dozens of other chemical and refining companies operate massive facilities along the Neches and Sabine Rivers. Engineers, safety inspectors, project managers, and procurement specialists from these facilities travel internationally on short notice — to chemical plants in Germany, refineries in Saudi Arabia, supplier facilities in the Netherlands, and joint venture operations in South Korea. These deployments often arrive with 10–14 days of notice, requiring expedited passport processing.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Lamar University in Beaumont has a growing international student population and sends hundreds of students abroad each year through exchange programs, study abroad semesters, and internships with international companies in the chemical engineering industry. Lamar's engineering school has particularly strong industry ties that create overseas internship placements — some with only weeks of notice.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Port of Beaumont is one of the busiest ports in the United States, ranking as a strategic seaport for military cargo. International maritime commerce through the port creates a steady stream of maritime industry professionals — maritime officers, port agents, and shipping company representatives — who need current passports for boarding international vessels and visiting foreign ports.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Southeast Texas residents also have convenient access to the Port of Galveston, approximately 75 miles west on I-10. Beaumont-area families regularly book Galveston cruises for Western Caribbean and Mexican Riviera sailings. The combination of short-notice cruise deals and children who have never had passports creates frequent passport urgencies in the Golden Triangle market.
          </p>
        </div>
      </div>
    </section>
  );
}

function BeaumontTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Beaumont Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">BPT — Southeast Texas Regional Airport</h3>
                <p className="text-gray-400 text-xs">Beaumont/Port Arthur airport with Houston connections</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Southeast Texas Regional Airport connects Beaumont to Houston's George Bush Intercontinental Airport (IAH) and Hobby Airport (HOU). International travelers from Beaumont typically connect through IAH for flights to Europe, Asia, and Latin America. Houston is also accessible by car — approximately 85 miles on I-10 — making George Bush Intercontinental a viable option for Beaumont residents with international departures.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Beaumont travelers connecting through IAH still need valid passports at their first point of departure in Southeast Texas. Don't assume your connection point is where passport checks happen.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-4-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Golden Triangle Petrochemical Industry</h3>
                <p className="text-gray-400 text-xs">ExxonMobil, Chevron Phillips, BASF, and dozens of chemical operations</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">The petrochemical refining complex along the Neches and Sabine Rivers is the dominant employer in the Golden Triangle. Specialty chemical engineers, turnaround contractors, and project managers travel internationally with short notice — to chemical plants in Germany, refineries in Saudi Arabia, and manufacturing facilities in South Korea. These deployments have fixed start dates that cannot be rescheduled, making passport urgency a recurring issue in the Beaumont job market.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Petrochemical plant turnarounds often bring in international contractors. If your project involves international travel, keep your passport current year-round — not just when a deployment comes up.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Port of Galveston Cruise Departures</h3>
                <p className="text-gray-400 text-xs">~75 miles west on I-10, Western Caribbean sailings</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Beaumont-area families drive to Galveston for Royal Caribbean, Carnival, and Disney cruise departures. The 75-mile drive on I-10 makes Galveston a popular choice for Southeast Texas families seeking affordable Western Caribbean and Mexico sailings. Many families book cruises with 3–6 weeks of lead time, discovering expired passports or children who have never had them. We process dozens of Golden Triangle cruise-related passport requests each month.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Even closed-loop Western Caribbean cruises strongly recommend passports for all passengers. Disney Cruise Line requires them for every passenger, including infants.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Lamar University Study Abroad</h3>
                <p className="text-gray-400 text-xs">Chemical engineering internships, exchange programs, international research</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Lamar University's engineering programs have strong ties to the Golden Triangle petrochemical industry, which extends to international companies and internship placements abroad. Students pursuing chemical engineering, process engineering, and environmental science degrees often land international internships with BASF in Germany, Shell in the Netherlands, or LyondellBasell facilities in Europe. These placements can surface with 4–6 weeks of lead time, requiring expedited passport processing.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Lamar University first-time applicants must visit an acceptance facility in person. We help students find the facility with the earliest available appointment so they don't miss their program start date.</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-anchor-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Port of Beaumont &amp; Maritime Industry</h3>
            <p className="text-white/60 text-sm leading-relaxed">The Port of Beaumont is a strategic military seaport and major commercial port. Maritime officers, port agents, and international shipping professionals based in Beaumont maintain current passports for boarding international vessels and visiting foreign ports. We provide fast passport processing for Beaumont's maritime community.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function BeaumontDestinationsSection() {
  const destinations = [
    { name: "Germany & Netherlands", context: "BASF Ludwigshafen, Shell Pernis, chemical industry plant inspections and turnaround work", urgency: "Chemical plant turnaround schedules are fixed and cannot be rescheduled", icon: "ri-map-pin-2-line" },
    { name: "Saudi Arabia & UAE", context: "Aramco refinery work, Abu Dhabi National Energy contracts, Gulf petrochemical partnerships", urgency: "Government contract work in Saudi Arabia often has 2-week visa processing timelines", icon: "ri-map-pin-2-line" },
    { name: "Mexico", context: "Family heritage trips, Monterrey business travel, Gulf Coast medical tourism, Cancun vacations", urgency: "Spring break and summer bookings peak February through June", icon: "ri-map-pin-2-line" },
    { name: "Caribbean & Bahamas", context: "Galveston cruise departures, Western Caribbean resort vacations, spring break cruises", urgency: "Galveston cruise bookings often happen 3–4 weeks out with family deal packages", icon: "ri-map-pin-2-line" },
    { name: "South Korea", context: "LyondellBasell and Hanwha Solutions industry travel, chemical engineering partnerships", urgency: "Industry conference and partnership meetings are scheduled months ahead with fixed dates", icon: "ri-map-pin-2-line" },
    { name: "Canada", context: "Beaumont-to-Canada energy industry travel, pipeline and refinery cross-border consulting", urgency: "Cross-border energy consulting often booked 1–3 weeks out", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "45%", label: "of Beaumont expedited requests are petrochemical or industrial industry-related" },
    { stat: "2–4 Weeks", label: "average lead time for Beaumont petrochemical deployment orders" },
    { stat: "75 Miles", label: "Beaumont's distance from the Port of Galveston — closest regional cruise terminal" },
    { stat: "85 Miles", label: "Beaumont's distance from Houston's IAH — nearest major international airport hub" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Beaumont Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Southeast Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/houston" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Houston <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/pasadena-tx" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Pasadena <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/corpus-christi" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Corpus Christi <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BeaumontTestimonialsSection() {
  const testimonials = [
    { name: "Mike D.", location: "ExxonMobil, Beaumont", text: "Had a refinery inspection in Rotterdam in 11 days and my passport expired. They processed it in 48 hours. The document review caught an issue I would have missed entirely.", rating: 5 },
    { name: "The Johnson Family", location: "Port Arthur", text: "We booked a Galveston Disney cruise and realized our kids had expired passports. Fast Passport Center processed all three in 6 days. Our cruise was saved.", rating: 5 },
    { name: "Dr. Martinez", location: "Lamar University", text: "I had a conference presentation in Seoul in 10 days and my passport needed renewal. They handled the process completely remotely. Received it in 5 days.", rating: 5 },
    { name: "Sandra T.", location: "Nederland, TX", text: "My husband's passport was damaged by flood water. Fast Passport Center guided us through the replacement process and got the new passport in 7 days. Very professional.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Beaumont Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeaumontAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Beaumont Main Post Office", address: "345 Mulberry St, Beaumont, TX 77701", phone: "(409) 832-8502", photo: "Yes", appointment: "Required" },
    { name: "North Park Station", address: "4455 Calder Ave, Beaumont, TX 77706", phone: "(409) 860-4455", photo: "Yes", appointment: "Walk-in" },
    { name: "Jefferson County Clerk", address: "1085 Pearl St, Beaumont, TX 77701", phone: "(409) 835-8475", photo: "No", appointment: "Required" },
    { name: "Port Arthur Post Office", address: "345 Spur 93, Port Arthur, TX 77640", phone: "(409) 982-7511", photo: "Yes", appointment: "Walk-in" },
    { name: "Nederland Post Office", address: "215 N Twin City Hwy, Nederland, TX 77627", phone: "(409) 722-4404", photo: "Yes", appointment: "Walk-in" },
    { name: "Lumberton Post Office", address: "780 N Main St, Lumberton, TX 77657", phone: "(409) 755-0781", photo: "No", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Beaumont Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility. Here are Golden Triangle locations with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. Call ahead to confirm availability. We can help find the earliest available slot across all Golden Triangle facilities.</p>
        </div>
      </div>
    </section>
  );
}

function BeaumontComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Beaumont Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Houston Agency (85 miles)</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "Same day (emergencies only)" },
                { feature: "Drive Required", us: "No", post: "Local only", reseller: "85-mile drive" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "In-person only" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Agency fees + travel" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Agency walk-in" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "In-person at agency" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "N/A" },
                { feature: "Golden Triangle Knowledge", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function BeaumontMistakesSection() {
  const mistakes = [
    { title: "Assuming Houston is the Only Option", desc: "Many Beaumont residents believe they must drive 85 miles to Houston for urgent passport processing. Our courier service delivers results faster than the Houston Passport Agency — without leaving Beaumont." },
    { title: "Not Accounting for Flood Damage", desc: "Southeast Texas flooding can damage passports stored at home. Water-damaged passports are considered invalid. Keep your passport in a waterproof document safe and check its condition after any flooding." },
    { title: "Using DS-82 When DS-11 Is Required", desc: "Beaumont applicants often attempt the simpler renewal form when their passport was issued at 15 or younger. This causes rejection and adds 1–2 weeks. We review before you submit." },
    { title: "6-Month Validity Rule for Petrochemical Travel", desc: "Saudi Arabia, UAE, and South Korea all require 6 months of passport validity beyond the return date. Beaumont petrochemical travelers who book international contracts without checking this are frequently caught short." },
    { title: "No Parental Consent for Minors", desc: "Golden Triangle families frequently overlook the DS-3053 form when one parent is deployed or unavailable. This is an automatic rejection — we guide you through the process correctly." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Beaumont Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeaumontHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Beaumont-area acceptance facility. We find the nearest one with the earliest appointment." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed package overnight to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. Full tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Delivered to your Beaumont address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Beaumont Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeaumontFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Beaumont Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeaumontFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Beaumont Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you have a petrochemical deployment in 3 days or a Galveston cruise in 3 weeks, we will get your passport processed on time. No 85-mile drive to Houston required.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function BeaumontPage() {
  useEffect(() => {
    document.title = "Emergency Passport Beaumont, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/beaumont/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Beaumont, TX — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Golden Triangle petrochemical workers, Lamar University students, Port Arthur families, and Southeast Texas travelers. No Houston drive required.");
    setMeta("geo.placename", "Beaumont, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "30.0802;-94.1266");
    setMeta("ICBM", "30.0802, -94.1266");
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <BeaumontHeroSection />
      <BeaumontStickyNav />
      <BeaumontProcessingTimeSection />
      <BeaumontServicesSection />
      <BeaumontLocalAreaSection />
      <BeaumontTravelTimingSection />
      <BeaumontDestinationsSection />
      <BeaumontTestimonialsSection />
      <BeaumontAcceptanceFacilitiesSection />
      <BeaumontComparisonSection />
      <BeaumontMistakesSection />
      <BeaumontHowItWorksSection />
      <BeaumontFAQSection />
      <SEOCrossLinks currentRoute="/cities/beaumont" state="TX" />
      <BeaumontFinalCTA />
    </div>
  );
}